import Image from "next/image";
import React from "react";
import { LogoWhite } from "@/assets";
import Link from "next/link";
import { Constant } from "@/constants/constants";
import { LiveChatButton, ButtonCta } from "@/components";

const services = [
  ["Book Design", "/book-design"],
  ["Book Editing", "/book-editing"],
  ["Book Publishing", "/book-publishing"],
  ["Book Marketing", "/book-marketing"],
  ["Book Writing", "/book-writing"],
  ["Children Book Writing", "/children-book-writing"],
];

const quickLinks = [
  ["Home", "/"],
  ["About Us", "/about-us"],
  ["Contact Us", "/contact-us"],
  ["FAQs", "/faq"],
];

function Footer() {
  return (
    <footer className="flex flex-col items-center justify-center">
      {/* CTA band */}
      <div className="w-full bg-brand-800 relative overflow-hidden">
        <div className="pointer-events-none absolute -left-10 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl animate-float-slow" />
        <div className="pointer-events-none absolute -right-6 bottom-0 h-40 w-40 rounded-full bg-gold-soft/20 blur-3xl animate-float" />
        <div className="relative mx-auto flex max-w-[1350px] flex-col items-center justify-between gap-6 px-5 py-12 text-center md:flex-row md:text-left">
          <div>
            <span className="eyebrow text-white/90">Let's Begin</span>
            <h3 className="mt-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Ready to bring your book to life?
            </h3>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <ButtonCta
              buttonText="Start Your Project"
              classStyle="bg-white text-brand-700 hover:bg-white/90"
            />
            <LiveChatButton
              buttonText="Live Chat"
              classStyle="bg-transparent text-white border border-white/50 hover:bg-white/10"
            />
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="w-full bg-ink text-paper">
        <div className="mx-auto grid max-w-[1350px] grid-cols-1 gap-10 px-6 py-16 sm:grid-cols-2 md:grid-cols-4">
          <div className="sm:col-span-2 md:col-span-1">
            <Image
              className="w-48 object-contain brightness-0 invert"
              src={LogoWhite}
              alt="Prime Publishing Authors"
            />
            <p className="mt-5 text-sm leading-relaxed text-paper/70">
              A collection of passionate, experienced writers, editors, and
              designers dedicated to the craft of storytelling — with a focus on
              quality, creativity, and author collaboration.
            </p>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-white">
              Quick Links
            </h4>
            <span className="mt-2 block h-px w-10 bg-gold/60" />
            <ul className="mt-5 space-y-3 text-sm text-paper/75">
              {quickLinks.map(([label, href]) => (
                <li key={href}>
                  <Link href={href} className="link-underline transition-colors hover:text-gold">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-white">
              Services
            </h4>
            <span className="mt-2 block h-px w-10 bg-gold/60" />
            <ul className="mt-5 space-y-3 text-sm text-paper/75">
              {services.map(([label, href]) => (
                <li key={href}>
                  <Link href={href} className="link-underline transition-colors hover:text-gold">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-white">
              Get in Touch
            </h4>
            <span className="mt-2 block h-px w-10 bg-gold/60" />
            <ul className="mt-5 space-y-3 text-sm text-paper/75">
              <li>
                <Link href={Constant.PhoneNumberLink} className="transition-colors hover:text-gold">
                  {Constant.PhoneNumber}
                </Link>
              </li>
              <li>
                <Link href={Constant.EmailLink} className="break-all transition-colors hover:text-gold">
                  {Constant.Email}
                </Link>
              </li>
              <li className="leading-relaxed">{Constant.Address}</li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-paper/10">
          <div className="mx-auto max-w-[1350px] px-6 py-6 space-y-3">
            <h4 className="text-sm font-semibold text-gold-soft">Disclaimer</h4>
            <p className="text-xs leading-relaxed text-paper/60">
              {Constant.CompanyName} operates as an independent entity
              specializing in book writing, publishing, and marketing services.
              All services are offered with full transparency and truth. We
              provide support to authors in publishing and marketing their books,
              but results may vary based on individual engagement, market
              conditions, and external factors.
            </p>
            <p className="text-xs leading-relaxed text-paper/60">
              {Constant.LegalDisclaimer}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="w-full bg-[#000000] text-paper">
        <div className="mx-auto flex max-w-[1350px] flex-col items-center justify-between gap-2 px-6 py-4 text-sm md:flex-row">
          <p className="text-paper/70">
            © 2026 Prime Publishing Authors. All rights reserved.
          </p>
          <div className="text-paper/70">
            <Link href="/privacy-policy" className="transition-colors hover:text-gold">
              Privacy Policy
            </Link>
            <span className="px-2 text-paper/30">|</span>
            <Link href="/terms-and-conditions" className="transition-colors hover:text-gold">
              Terms of Service
            </Link>
            <span className="px-2 text-paper/30">|</span>
            <Link href="/disclaimer" className="transition-colors hover:text-gold">
              Disclaimer
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
